<template>
  <div class="w-full flex justify-center gap-2">
    <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 p-4 min-h-fit">
      <div class="h-6 shrink-0">
        <span class="text-black-white text-l-medium">{{ $t('keys.rs.title') }}</span>
      </div>
      <div class="flex-1 flex flex-col justify-evenly items-center">
        <!-- W & S -->
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-6">
            <Button color="glass" shape="rect" active class="w-12 h-12">W</Button>
            <Icon icon="Function-Wireframe-HorizontalBind" class="w-6 h-6 text-black-white" />
            <Button color="glass" shape="rect" active class="w-12 h-12">S</Button>
          </div>
          <div>
            <Switch v-model="wnsToggler" size="lg" @update:model-value="handleChangeWnS" />
          </div>
        </div>
        <!-- A & D -->
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-6">
            <Button color="glass" shape="rect" active class="w-12 h-12">A</Button>
            <Icon icon="Function-Wireframe-HorizontalBind" class="w-6 h-6 text-black-white" />
            <Button color="glass" shape="rect" active class="w-12 h-12">D</Button>
          </div>
          <div>
            <Switch v-model="andToggler" size="lg" @update:model-value="handleChangeAnD" />
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col glass-frosted relative rounded-8xl w-250 h-67.5 p-4 min-h-fit">
      <div class="p-4 flex flex-col gap-1 h-20 shrink-0 bg-key rounded-4xl">
        <div class="flex items-center justify-between">
          <div class="flex items-center text-black-white">
            <Icon icon="Technology-Wireframe-QuickTrigger" class="w-6 h-6" />
            <span class="text-m-medium">{{ $t('keys.rs.quickTrigger') }}</span>
          </div>
          <div>
            <Switch
              v-model="rtEnabled"
              :disabled="!wnsToggler && !andToggler"
              @update:model-value="handleChangeRTEnabled"
            />
          </div>
        </div>
        <p class="text-text-glass-default text-s-medium">
          {{ $t('keys.rs.quickTriggerDescription') }}
        </p>
      </div>
      <div class="p-4 mt-2 flex-1 flex flex-col gap-1 bg-key rounded-4xl">
        <div class="flex items-center text-black-white">
          <Icon icon="Function-Wireframe-CurrentClick" class="w-6 h-6" />
          <span class="text-m-medium">{{ $t('keys.rs.executePoint') }}</span>
        </div>
        <p class="text-text-glass-default text-s-medium">
          {{ $t('keys.rs.executePointDescription') }}
        </p>
        <div class="flex items-start gap-3 w-full">
          <!-- Min label -->
          <span class="text-black-white text-s-medium shrink-0">{{ MIN }}</span>

          <div class="relative flex-1 flex items-start h-16">
            <!-- 左侧死区：锯齿箭头装饰 -->
            <div class="shrink-0 flex items-center h-5 overflow-hidden" :style="{ width: '12%' }">
              <DeadZonePattern direction="left" />
            </div>

            <!-- 中间真实 Slider -->
            <div class="relative flex-1 h-5 flex items-center">
              <Slider
                v-model="value"
                :min="SLIDE_MIN"
                :max="SLIDE_MAX"
                :step="1"
                :disabled="!wnsToggler && !andToggler"
                class="w-full"
                track-class="bg-glass30"
                range-class="bg-brand-blue1 disabled:bg-disabled"
                thumb-class="bg-white"
                @value-commit="handleSliderValueCommit"
              />
              <div
                class="absolute top-full left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
                :style="{ left: bubbleLeft }"
              >
                <div
                  :class="`text-brand-blue1 text-l-medium ${!wnsToggler && !andToggler && 'text-text-disabled!'}`"
                >
                  {{ currentValue }}
                </div>
                <div
                  :class="`text-text-glass-default text-s-medium ${!wnsToggler && !andToggler && 'text-text-disabled!'}`"
                >
                  (~{{ mmValue }}mm)
                </div>
              </div>
            </div>

            <!-- 右侧死区：锯齿箭头装饰 -->
            <div class="shrink-0 flex items-center h-5 overflow-hidden" :style="{ width: '24%' }">
              <DeadZonePattern direction="right" />
            </div>
          </div>

          <!-- Max label -->
          <span class="text-black-white text-s-medium shrink-0">{{ MAX }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, ref, watch } from 'vue'
  import { Button, Icon, Slider, Switch } from '@rapoo/element'
  import { KEYCODE_KEYWORDS_MAP } from '../utils/keyMapSettings'
  import { parseNumber } from '../utils/keyIdentity'
  import {
    KeySettingsSymbol,
    RestoreKeyMappingWithTargetSymbol,
    UpdateKeyMappingWithTargetSymbol,
  } from '../types'
  import DeadZonePattern from './DeadZonePattern.vue'

  import type { KeyConfig } from '@rapoo/element'
  import type { RS } from '@rapoo/device-sdk'
  import type {
    KeyChangePayload,
    KeySetting,
    RestoreKeyMappingWithTarget,
    UpdateKeyMappingWithTarget,
  } from '../types'

  type RSKeywords = 'W' | 'S' | 'A' | 'D'
  type RSPair = readonly [RSKeywords, RSKeywords]

  interface RSItem {
    keywords: RSKeywords
    index: number
  }

  // 网吧键盘固定四个按键，后续可能不固定，需要提供keywords来查找
  // index 为布局键的横向索引（key 字段），W=2*22+2、S/A/D 同理。UI 层只使用 index。
  const RS_KEYS: Record<RSKeywords, RSItem> = {
    W: { keywords: 'W', index: 46 },
    S: { keywords: 'S', index: 68 },
    A: { keywords: 'A', index: 67 },
    D: { keywords: 'D', index: 69 },
  }

  const WNS_PAIR: RSPair = ['W', 'S']
  const AND_PAIR: RSPair = ['A', 'D']

  const MIN = 0
  const MAX = 255
  const SLIDE_MIN = 22
  const SLIDE_MAX = 204
  const THUMB_SIZE = 12
  const DEFAULT_SLIDER_VALUE = 36
  const THRESHOLD_MM_MAX = 3.5

  const keySettings = inject(KeySettingsSymbol)
  const updateKeyMappingWithTarget = inject<UpdateKeyMappingWithTarget>(
    UpdateKeyMappingWithTargetSymbol,
  )
  const restoreKeyMappingWithTarget = inject<RestoreKeyMappingWithTarget>(
    RestoreKeyMappingWithTargetSymbol,
  )

  const wnsToggler = ref(false)
  const andToggler = ref(false)
  const rtEnabled = ref(false)

  const value = ref([DEFAULT_SLIDER_VALUE])
  const currentValue = computed(() => value.value[0] ?? SLIDE_MIN)

  // 将滑块值转换为毫米单位
  const mmValue = computed(() => ((currentValue.value / MAX) * THRESHOLD_MM_MAX).toFixed(2))
  const triggerThreshold = computed(() =>
    Math.round((currentValue.value / MAX) * THRESHOLD_MM_MAX * 100),
  )

  const sliderProgress = computed(() => {
    const ratio = (currentValue.value - SLIDE_MIN) / (SLIDE_MAX - SLIDE_MIN)
    return Math.min(1, Math.max(0, ratio))
  })

  const bubbleLeft = computed(() => {
    const progress = sliderProgress.value
    const offset = (0.5 - progress) * THUMB_SIZE
    return `calc(${progress * 100}% + ${offset}px)`
  })

  // 将RS阈值（0.01毫米）转换回0-255的滑块数值。
  const parseSliderValueByThreshold = (threshold: unknown) => {
    const parsed =
      typeof threshold === 'number' ? threshold : Number.parseInt(String(threshold ?? ''), 10)

    if (Number.isNaN(parsed)) return DEFAULT_SLIDER_VALUE

    const slider = Math.round((parsed / (THRESHOLD_MM_MAX * 100)) * MAX)
    return Math.min(MAX, Math.max(MIN, slider))
  }

  // 查找当前按键的 RS 设置
  const getRSSettingByKey = (settings: KeySetting[], keywords: RSKeywords) => {
    return settings.find((item) => item.keyType === 'RS' && item.keywords === keywords)
  }

  // 查看按键是否启用 RS 设置
  const hasPairRSSetting = (settings: KeySetting[], pair: RSPair) => {
    return Boolean(getRSSettingByKey(settings, pair[0]) && getRSSettingByKey(settings, pair[1]))
  }

  // 同步状态到 UI.
  const syncUIBySettings = (settings: KeySetting[]) => {
    const hasWnS = hasPairRSSetting(settings, WNS_PAIR)
    const hasAnD = hasPairRSSetting(settings, AND_PAIR)

    if (!hasWnS && !hasAnD) {
      wnsToggler.value = false
      andToggler.value = false
      rtEnabled.value = false
      value.value = [DEFAULT_SLIDER_VALUE]
      return
    }

    const activePair = hasWnS ? WNS_PAIR : AND_PAIR
    wnsToggler.value = activePair === WNS_PAIR
    andToggler.value = activePair === AND_PAIR

    const activeSetting =
      getRSSettingByKey(settings, activePair[0]) ?? getRSSettingByKey(settings, activePair[1])
    const activeRS = activeSetting?.raw?.rs

    rtEnabled.value = activeRS?.combineRT === 1
    value.value = [parseSliderValueByThreshold(activeRS?.triggerThreshold)]
  }

  watch(
    () => keySettings?.value,
    (settings) => {
      syncUIBySettings((settings ?? []) as KeySetting[])
    },
    { immediate: true, deep: true },
  )

  // 构建 keyConfig 配置给 update/restore API (keyMapSetting).
  const buildTargetKey = (item: RSItem) => {
    const config = KEYCODE_KEYWORDS_MAP.get(item.keywords)

    if (KEYCODE_KEYWORDS_MAP.has(item.keywords)) {
      return {
        ...config,
        key: item.index,
      }
    }

    return null
  }

  // 发送绑定键单个按键请求
  const setRSForKey = (triggerKey: RSItem, relatedKey: RSItem) => {
    if (!updateKeyMappingWithTarget) return

    const targetKey = buildTargetKey(triggerKey)

    const rs: RS = {
      combineRT: rtEnabled.value ? 1 : 0,
      relatedKeyIndex: relatedKey.index,
      triggerKeyType: 0x00,
      triggerKeyCode: parseNumber(targetKey?.code) || 0,
      triggerKeyExtCode: 0x00,
      triggerThreshold: triggerThreshold.value,
    }

    const payload: KeyChangePayload = {
      keywords: triggerKey.keywords,
      keyType: 'RS',
      target: null,
      raw: {
        type: 0x0d,
        rs,
      },
    }

    updateKeyMappingWithTarget(payload, targetKey as KeyConfig)
  }

  // 发送绑定键位改键
  const applyPair = (pair: RSPair) => {
    const first = RS_KEYS[pair[0]]
    const second = RS_KEYS[pair[1]]

    setRSForKey(first, second)
    setRSForKey(second, first)
  }

  // 恢复两个绑定按键
  const restoreKeyByCode = (keywords: RSKeywords) => {
    if (!restoreKeyMappingWithTarget) return
    restoreKeyMappingWithTarget(buildTargetKey(RS_KEYS[keywords]) as KeyConfig)
  }

  // 恢复按键.
  const restorePair = (pair: RSPair) => {
    restoreKeyByCode(pair[0])
    restoreKeyByCode(pair[1])
  }

  // 右侧两个表单改变时重新处理改键
  const applyCurrentActivePair = () => {
    if (wnsToggler.value) {
      applyPair(WNS_PAIR)
      return
    }

    if (andToggler.value) {
      applyPair(AND_PAIR)
    }
  }

  const handleChangeWnS = (nextValue: boolean) => {
    wnsToggler.value = nextValue
    if (!nextValue) {
      restorePair(WNS_PAIR)
      return
    }

    if (andToggler.value) {
      andToggler.value = false
      restorePair(AND_PAIR)
    }

    applyPair(WNS_PAIR)
  }

  const handleChangeAnD = (nextValue: boolean) => {
    andToggler.value = nextValue
    if (!nextValue) {
      restorePair(AND_PAIR)
      return
    }

    if (wnsToggler.value) {
      wnsToggler.value = false
      restorePair(WNS_PAIR)
    }

    applyPair(AND_PAIR)
  }

  const handleChangeRTEnabled = (nextValue: boolean) => {
    rtEnabled.value = nextValue
    applyCurrentActivePair()
  }

  // 当 Slider 滑动释放时进行改键
  const handleSliderValueCommit = (nextValue: number[] | undefined) => {
    if (nextValue?.length) {
      value.value = nextValue
    }
    applyCurrentActivePair()
  }
</script>

<style scoped></style>
